import React, { useState } from 'react'
import { Button } from 'flowbite-react';
import { useAccountApi } from '../../store';

interface Props {
    user: any;
    onUserUpdate?: (updatedUser: any) => void;
}

const Field = ({ label, value }: { label: string; value?: any }) => (
    <div className="px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-900">{label}</dt>
        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
            {value !== null && value !== undefined && value !== '' ? String(value) : <span className="text-gray-400 italic">—</span>}
        </dd>
    </div>
);

const statusBadge = (s: string) => {
    const colors: Record<string, string> = {
        active:    'bg-green-100 text-green-800',
        suspended: 'bg-red-100 text-red-800',
        deleted:   'bg-gray-200 text-gray-600',
        email_verification: 'bg-yellow-100 text-yellow-800',
    };
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${colors[s] ?? 'bg-gray-100 text-gray-600'}`}>
            {s}
        </span>
    );
};

const UserInfo: React.FC<Props> = ({ user: initialUser, onUserUpdate }) => {
    const { updateUserStatus } = useAccountApi();
    const [user, setUser] = useState<any>(initialUser);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [confirmAction, setConfirmAction] = useState<'suspend' | 'activate' | null>(null);

    const handleStatusChange = async (newStatus: 'active' | 'suspended') => {
        setConfirmAction(null);
        setActionLoading(true);
        setActionMessage(null);
        try {
            const result = await updateUserStatus({ userId: user.id, status: newStatus });
            if (result?.res === 'success') {
                const updated = { ...user, status: newStatus };
                setUser(updated);
                onUserUpdate?.(updated);
                setActionMessage({ type: 'success', text: `User status updated to "${newStatus}".` });
            } else {
                setActionMessage({ type: 'error', text: result?.message || 'Failed to update status.' });
            }
        } catch {
            setActionMessage({ type: 'error', text: 'An unexpected error occurred.' });
        } finally {
            setActionLoading(false);
        }
    };

    const canSuspend  = user.status === 'active';
    const canActivate = user.status === 'suspended' || user.status === 'deleted';

    return (
        <div className="mt-4 overflow-hidden bg-white shadow sm:rounded-lg">
            {/* Header with status badge */}
            <div className="px-4 py-4 sm:px-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h3 className="text-base font-semibold leading-6 text-gray-900">
                        {user.displayName || `${user.firstName} ${user.lastName}`}
                    </h3>
                    <p className="mt-0.5 text-sm text-gray-500">@{user.username}</p>
                </div>
                {statusBadge(user.status)}
            </div>

            {/* Action buttons */}
            {(canSuspend || canActivate) && (
                <div className="border-t border-gray-100 px-4 py-4 sm:px-6">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                        Account Actions
                    </p>

                    {actionMessage && (
                        <div className={`mb-3 rounded-md px-3 py-2 text-sm ${
                            actionMessage.type === 'success'
                                ? 'bg-green-50 text-green-800'
                                : 'bg-red-50 text-red-700'
                        }`}>
                            {actionMessage.text}
                        </div>
                    )}

                    {confirmAction && (
                        <div className="mb-3 rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2">
                            <p className="text-sm text-yellow-800 mb-2">
                                Are you sure you want to <strong>{confirmAction}</strong> this user?
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    size="xs"
                                    color={confirmAction === 'suspend' ? 'failure' : 'success'}
                                    onClick={() => handleStatusChange(confirmAction === 'suspend' ? 'suspended' : 'active')}
                                    disabled={actionLoading}
                                >
                                    {actionLoading ? 'Processing…' : 'Confirm'}
                                </Button>
                                <Button
                                    size="xs"
                                    color="light"
                                    onClick={() => setConfirmAction(null)}
                                    disabled={actionLoading}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    )}

                    {!confirmAction && (
                        <div className="flex gap-2 flex-wrap">
                            {canSuspend && (
                                <Button
                                    size="sm"
                                    color="failure"
                                    onClick={() => { setActionMessage(null); setConfirmAction('suspend'); }}
                                    disabled={actionLoading}
                                >
                                    Suspend Account
                                </Button>
                            )}
                            {canActivate && (
                                <Button
                                    size="sm"
                                    color="success"
                                    onClick={() => { setActionMessage(null); setConfirmAction('activate'); }}
                                    disabled={actionLoading}
                                >
                                    Reactivate Account
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* User details */}
            <div className="border-t border-gray-100">
                <dl className="divide-y divide-gray-100">
                    <Field label="User ID"       value={String(user.id).slice(-12)} />
                    <Field label="Username"      value={user.username} />
                    <Field label="Display name"  value={user.displayName} />
                    <Field label="First name"    value={user.firstName} />
                    <Field label="Last name"     value={user.lastName} />
                    <Field label="Email"         value={user.email} />
                    <Field label="Phone"         value={user.phone} />
                    <Field label="Country"       value={user.country} />
                    <Field label="Gender"        value={user.gender} />
                    <Field label="User Type"     value={user.userType} />
                    <Field label="Status"        value={user.status} />
                    <Field label="Cash Balance"  value={user.cashBalance !== undefined ? `$${user.cashBalance}` : undefined} />
                    <Field label="Bonus Total"   value={user.bonusTotal} />
                    <Field label="Ambassador"    value={user.ambassador} />
                    <Field label="Followers"     value={user.followers} />
                    <Field label="Following"     value={user.followees} />
                    <Field label="Rating (hosts)"  value={user.ratingFromHosts} />
                    <Field label="Rating (users)"  value={user.ratingFromUsers} />
                    <Field label="Last Login"    value={user.last_login} />
                    <Field label="Created"       value={user.created_at} />
                    <Field label="Updated"       value={user.updated_at} />
                </dl>
            </div>
        </div>
    );
};

export default UserInfo;
